import React, { useState, useEffect, useCallback } from 'react';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import GoalCard from './components/GoalCard';
import AddGoalModal from './components/AddGoalModal';
import ContributeModal from './components/ContributeModal';
import GoalDetailDrawer from './components/GoalDetailDrawer';
import SmartSuggestions from './components/SmartSuggestions';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import ContributionOptimizer from './components/ContributionOptimizer';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Goals' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' },
];

export default function FinancialGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [contributeGoal, setContributeGoal] = useState(null);
  const [detailGoal, setDetailGoal] = useState(null);
  const [deleteGoal, setDeleteGoal] = useState(null);

  const fetchGoals = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase?.from('financial_goals')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });
      if (err) throw err;
      // Map DB columns to component-friendly shape (add type from icon/color heuristic)
      const mapped = (data || [])?.map(g => ({
        ...g,
        type: g?.type || deriveType(g?.icon),
      }));
      setGoals(mapped);
    } catch (err) {
      setError(err?.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  function deriveType(icon) {
    const map = { Plane: 'vacation', Shield: 'emergency_fund', CreditCard: 'debt_repayment', Home: 'home_down_payment' };
    return map?.[icon] || 'custom';
  }

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase?.channel('financial_goals_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'financial_goals', filter: `user_id=eq.${user?.id}` }, () => {
        fetchGoals();
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [user?.id, fetchGoals]);

  const handleSaveGoal = async (formData) => {
    if (!user?.id) throw new Error('Not authenticated');
    if (editGoal) {
      const { error: err } = await supabase?.from('financial_goals')?.update({
          title: formData?.title,
          type: formData?.type,
          icon: formData?.icon,
          color: formData?.color,
          target_amount: formData?.target_amount,
          current_amount: formData?.current_amount,
          deadline: formData?.deadline,
          monthly_contribution: formData?.monthly_contribution,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', editGoal?.id)?.eq('user_id', user?.id);
      if (err) throw err;
    } else {
      const { error: err } = await supabase?.from('financial_goals')?.insert({
          user_id: user?.id,
          title: formData?.title,
          type: formData?.type,
          icon: formData?.icon,
          color: formData?.color,
          target_amount: formData?.target_amount,
          current_amount: formData?.current_amount,
          deadline: formData?.deadline,
          monthly_contribution: formData?.monthly_contribution,
          goal_status: 'active',
        });
      if (err) throw err;
    }
    setEditGoal(null);
    await fetchGoals();
  };

  const handleContribute = async (goal, amount) => {
    if (!user?.id) throw new Error('Not authenticated');
    const newAmount = parseFloat(goal?.current_amount || 0) + amount;
    const isCompleted = newAmount >= parseFloat(goal?.target_amount || 0);
    const { error: err } = await supabase?.from('financial_goals')?.update({
        current_amount: newAmount,
        goal_status: isCompleted ? 'completed' : 'active',
        updated_at: new Date()?.toISOString(),
      })?.eq('id', goal?.id)?.eq('user_id', user?.id);
    if (err) throw err;
    await fetchGoals();
    // Update detail drawer if open
    if (detailGoal?.id === goal?.id) {
      setDetailGoal(prev => prev ? { ...prev, current_amount: newAmount, goal_status: isCompleted ? 'completed' : 'active' } : null);
    }
  };

  const handleDelete = async (goal) => {
    if (!user?.id) throw new Error('Not authenticated');
    const { error: err } = await supabase?.from('financial_goals')?.delete()?.eq('id', goal?.id)?.eq('user_id', user?.id);
    if (err) throw err;
    await fetchGoals();
  };

  const filteredGoals = goals?.filter(g => filter === 'all' || g?.goal_status === filter);

  const totalSaved = goals?.reduce((sum, g) => sum + parseFloat(g?.current_amount || 0), 0);
  const totalTarget = goals?.reduce((sum, g) => sum + parseFloat(g?.target_amount || 0), 0);
  const completedCount = goals?.filter(g => g?.goal_status === 'completed')?.length;
  const overallPct = totalTarget > 0 ? Math.min((totalSaved / totalTarget) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      <PrimaryNavigation />
      <main className="pt-6 pb-24 md:pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl text-[#F8FAFC]">Financial Goals</h1>
            <p className="text-sm text-[#64748B] mt-1">Track your progress toward financial milestones</p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <ContributionOptimizer
              goals={goals}
              onApplyAllocations={(allocations) => {
                // Pre-fill contribution amounts — open contribute modal for first goal
                if (allocations?.length > 0) {
                  const firstAlloc = allocations?.[0];
                  const matchedGoal = goals?.find(g => g?.title === firstAlloc?.goalTitle);
                  if (matchedGoal) setContributeGoal(matchedGoal);
                }
              }}
            />
            <button
              onClick={() => { setEditGoal(null); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#0F172A] transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #00E5FF, #06B6D4)' }}
            >
              <Icon name="Plus" size={16} color="#0F172A" />
              Add Goal
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Goals', value: goals?.length, icon: 'Target', color: '#00E5FF' },
            { label: 'Completed', value: completedCount, icon: 'CheckCircle', color: '#10B981' },
            { label: 'Total Saved', value: `$${totalSaved?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: 'PiggyBank', color: '#7B2FBE' },
            { label: 'Overall Progress', value: `${overallPct?.toFixed(0)}%`, icon: 'TrendingUp', color: '#F59E0B' },
          ]?.map((stat, i) => (
            <div key={i} className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat?.color}15` }}>
                  <Icon name={stat?.icon} size={14} color={stat?.color} />
                </div>
                <span className="text-xs text-[#64748B]">{stat?.label}</span>
              </div>
              <p className="font-data text-xl font-bold" style={{ color: stat?.color }}>{stat?.value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Filter Tabs */}
            <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
              {FILTER_OPTIONS?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setFilter(opt?.value)}
                  className="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200"
                  style={{
                    backgroundColor: filter === opt?.value ? 'rgba(0,229,255,0.12)' : 'transparent',
                    color: filter === opt?.value ? '#00E5FF' : '#64748B',
                    border: `1px solid ${filter === opt?.value ? 'rgba(0,229,255,0.3)' : 'rgba(6,182,212,0.1)'}`,
                  }}
                >
                  {opt?.label}
                  {opt?.value !== 'all' && (
                    <span className="ml-1.5 text-[10px]">
                      ({goals?.filter(g => g?.goal_status === opt?.value)?.length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-4 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl mb-5">
                <Icon name="AlertCircle" size={16} color="#EF4444" />
                <p className="text-sm text-[#EF4444]">{error}</p>
                <button onClick={fetchGoals} className="ml-auto text-xs text-[#64748B] hover:text-[#94A3B8] underline">Retry</button>
              </div>
            )}

            {/* Goals Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6]?.map(i => (
                  <div key={i} className="h-64 bg-[#0F172A] border border-[rgba(6,182,212,0.08)] rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredGoals?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[rgba(6,182,212,0.08)] flex items-center justify-center mb-4">
                  <Icon name="Target" size={28} color="#475569" />
                </div>
                <h3 className="font-heading font-semibold text-base text-[#94A3B8] mb-2">
                  {filter === 'all' ? 'No goals yet' : `No ${filter} goals`}
                </h3>
                <p className="text-sm text-[#475569] mb-5">
                  {filter === 'all' ? 'Start by adding your first financial goal.' : `You have no ${filter} goals at the moment.`}
                </p>
                {filter === 'all' && (
                  <button
                    onClick={() => { setEditGoal(null); setShowAddModal(true); }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-[#0F172A]"
                    style={{ background: 'linear-gradient(135deg, #00E5FF, #06B6D4)' }}
                  >
                    <Icon name="Plus" size={15} color="#0F172A" />
                    Add Your First Goal
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredGoals?.map(goal => (
                  <GoalCard
                    key={goal?.id}
                    goal={goal}
                    onClick={setDetailGoal}
                    onContribute={g => setContributeGoal(g)}
                    onEdit={g => { setEditGoal(g); setShowAddModal(true); }}
                    onDelete={setDeleteGoal}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar: Smart Suggestions */}
          <div className="lg:w-80 flex-shrink-0">
            <SmartSuggestions goals={goals} />
          </div>
        </div>
      </main>
      {/* Modals */}
      <AddGoalModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditGoal(null); }}
        onSave={handleSaveGoal}
        editGoal={editGoal}
      />
      <ContributeModal
        isOpen={!!contributeGoal}
        onClose={() => setContributeGoal(null)}
        onContribute={handleContribute}
        goal={contributeGoal}
      />
      <GoalDetailDrawer
        goal={detailGoal}
        isOpen={!!detailGoal}
        onClose={() => setDetailGoal(null)}
        onContribute={g => { setDetailGoal(null); setContributeGoal(g); }}
      />
      <DeleteConfirmModal
        isOpen={!!deleteGoal}
        onClose={() => setDeleteGoal(null)}
        onConfirm={handleDelete}
        goal={deleteGoal}
      />
    </div>
  );
}
